// 微前端环境下动态设置webpack publicPath
if ((window as any).__POWERED_BY_QIANKUN__) {
  // eslint-disable-next-line no-undef
  __webpack_public_path__ = (window as any).__INJECTED_PUBLIC_PATH_BY_QIANKUN__;
}

import { createApp, App as VueApp } from 'vue'
import App from './App.vue'
import router from './router'
import store from './store'
import dataV from '@jiaminghi/data-view'
import PublicComponent from '@/components/componentInstall'
import '@/assets/scss/style.scss'
import '@/assets/icon/iconfont.css'
import { setGlobalToken, setGlobalUserInfo, clearGlobalUserInfo, setGlobalIsAdmin } from '@/utils/request'
import { isDemoMode, getDemoDashboardData } from '@/utils/demoMode'

// 在开发环境中导入刷新示例，用于调试
if (process.env.NODE_ENV === 'development') {
  import('@/utils/refreshExample')
}

let vueApp: VueApp<Element> | null = null;
let wsCleanup: (() => void) | null = null;

/**
 * 达瑞大屏必须展示权限范围内的真实统计。token 刷新只会传递部分 props，
 * 因此缺少 isDaruiScreen 时沿用上一轮注入状态，避免演示数据覆盖真实 dashboardData。
 */
type DashboardMicroAppProps = Record<string, unknown>

function shouldUseDemoDashboardData(props: DashboardMicroAppProps): boolean {
  const hasDaruiFlag = Object.prototype.hasOwnProperty.call(props, 'isDaruiScreen')
  const injectedProps = (window as unknown as {
    __INJECTED_PROPS__?: DashboardMicroAppProps
  }).__INJECTED_PROPS__ || {}
  const isDaruiDashboard = hasDaruiFlag
    ? props.isDaruiScreen === true
    : injectedProps.isDaruiScreen === true
  return isDemoMode() && !isDaruiDashboard
}
// 创建应用实例
function render(props: any = {}) {
  const { container } = props
  vueApp = createApp(App)
  
  vueApp.use(PublicComponent)
  vueApp.use(dataV)
  vueApp.use(store)
  vueApp.use(router)
  
  // 🔴 关键修复：正确处理 qiankun 环境下的容器挂载
  // 在 qiankun 环境中，qiankun 会处理容器，但我们需要在容器内创建挂载点
  // 注意：container 可能在 props 中，也可能由 qiankun 自动处理
  let mountElement: string | Element = '#app';
  
  // 检查是否在 qiankun 环境中
  const isQiankunEnv = !!(window as any).__POWERED_BY_QIANKUN__;
  
  if (isQiankunEnv && container) {
    // qiankun 环境：在容器内部查找或创建 #app 元素
    let appElement = (container as HTMLElement).querySelector('#app');
    
    if (!appElement) {
      // 如果容器内没有 #app 元素，创建一个
      appElement = document.createElement('div');
      appElement.id = 'app';
      // 确保新创建的元素有合适的样式，占满容器
      (appElement as HTMLElement).style.width = '100%';
      (appElement as HTMLElement).style.height = '100%';
      
      // 将 #app 元素添加到容器中
      (container as HTMLElement).appendChild(appElement);
    }
    mountElement = appElement;
  } else if (isQiankunEnv && !container) {
    // qiankun 环境但没有 container，尝试从 DOM 中查找
    // 这通常不会发生，但作为备用方案
    console.warn('qiankun 环境但未提供 container，尝试查找默认容器');
    const defaultContainer = document.querySelector('#admin-microapp');
    if (defaultContainer) {
      let appElement = (defaultContainer as HTMLElement).querySelector('#app');
      if (!appElement) {
        appElement = document.createElement('div');
        appElement.id = 'app';
        (appElement as HTMLElement).style.width = '100%';
        (appElement as HTMLElement).style.height = '100%';
        (defaultContainer as HTMLElement).appendChild(appElement);
      }
      mountElement = appElement;
    }
  } 
  
  
  if (!mountElement) {
    const error = new Error('无法找到有效的挂载元素');
    console.error('❌ 挂载失败:', error);
    throw error;
  }
  
  try {
    vueApp.mount(mountElement)
  } catch (mountError: any) {
    console.error('❌ Vue 应用挂载失败:', mountError);
    console.error('挂载目标:', mountElement);
    throw mountError;
  }
}

// 如果不是在qiankun环境中运行，则直接启动应用
if (!(window as any).__POWERED_BY_QIANKUN__) {
  render()
}

// qiankun生命周期钩子 - bootstrap
export async function bootstrap() {
  // Vue应用启动完成
}

// qiankun生命周期钩子 - mount
export async function mount(props: any) {
  try {
    // 设置通过qiankun传递的token
    if (props.token) {
      setGlobalToken(props.token)
    }
    
    // 主应用每次 mount 传入的最新用户信息（不读本地缓存）
    if (Object.prototype.hasOwnProperty.call(props, 'userInfo')) {
      setGlobalUserInfo(props.userInfo)
    }

    if (Object.prototype.hasOwnProperty.call(props, 'isAdmin')) {
      setGlobalIsAdmin(!!props.isAdmin)
    }

    // 达瑞模式优先使用主应用注入的真实统计；仅普通演示账号保留 test.json。
    if (shouldUseDemoDashboardData(props)) {
      store.dispatch('parentData/setDashboardData', getDemoDashboardData())
    } else if (props.dashboardData) {
      store.dispatch('parentData/setDashboardData', props.dashboardData)
    }

    // 保存props到全局，供其他组件使用
    (window as any).__INJECTED_PROPS__ = props
    
    // 初始化websocket监听
    wsCleanup = await store.dispatch('websocket/initWebsocket')
    
    render(props)
  } catch (error: any) {
    console.error('❌ big_screen 微应用挂载失败:', error);
    console.error('错误详情:', {
      message: error?.message,
      stack: error?.stack,
      name: error?.name,
    });
    throw error;
  }
}

// qiankun生命周期钩子 - unmount
export async function unmount() {
  // 清理websocket监听器
  if (wsCleanup) {
    wsCleanup()
    wsCleanup = null
  }

  // 清空用户信息，避免下次挂载沿用旧数据
  clearGlobalUserInfo()
  ;(window as any).__INJECTED_PROPS__ = null

  if (vueApp) {
    vueApp.unmount()
    vueApp = null
  }
}

// qiankun 生命周期钩子 - update（接收主应用的实时 props 更新）
export async function update(props: any) {
  if (props.token) {
    setGlobalToken(props.token)
  }

  // 有 userInfo 字段时始终覆盖为最新值
  if (Object.prototype.hasOwnProperty.call(props, 'userInfo')) {
    setGlobalUserInfo(props.userInfo)
  }

  if (Object.prototype.hasOwnProperty.call(props, 'isAdmin')) {
    setGlobalIsAdmin(!!props.isAdmin)
  }

  if (shouldUseDemoDashboardData(props)) {
    store.dispatch('parentData/setDashboardData', getDemoDashboardData())
  } else if (props.dashboardData !== undefined) {
    store.dispatch('parentData/setDashboardData', props.dashboardData)
  }

  (window as any).__INJECTED_PROPS__ = {
    ...(window as any).__INJECTED_PROPS__,
    ...props,
  }
}
